/**
 * Create an additional-work offer from inspection recommendations (#3210).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import {
  workOrderOffersQueryKey,
  type WorkOrderOffer,
} from './useWorkOrderOffers';

export interface CreateInspectionOfferInput {
  inspectionId: string;
  recommendationIds: string[];
  title?: string;
  customerDescription?: string;
}

export interface InspectionOfferFinding {
  id: string;
  description: string;
  urgency: 'immediate' | 'soon' | 'future';
  estimatedPriceCents: number;
  workOrderItemId: string | null;
  offerId: string | null;
}

export function useCreateInspectionOffer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      inspectionId,
      recommendationIds,
      title,
      customerDescription,
    }: CreateInspectionOfferInput) =>
      api.post<WorkOrderOffer>(`/inspections/${inspectionId}/recommendations/offer`, {
        recommendationIds,
        ...(title != null ? { title } : {}),
        ...(customerDescription != null ? { customerDescription } : {}),
      }),
    onSuccess: (offer, { inspectionId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.detail(inspectionId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.inspections.recommendations(inspectionId),
      });
      if (offer.bookingId) {
        void queryClient.invalidateQueries({
          queryKey: workOrderOffersQueryKey(offer.bookingId),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.workOrders.detail(offer.bookingId),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.workOrders.lists(),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.invoicePreview(offer.bookingId),
        });
      }
    },
  });
}
